import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLock, faServer, faEnvelope, faCode } from '@fortawesome/free-solid-svg-icons';
import Logo from '../../images/Logo1.png';
import './Hero.scss';

/**
 * Hero component for the MET Connect® website
 * 
 * This component serves as the main banner on the homepage,
 * featuring key value propositions and call-to-action buttons
 * 
 * @returns {JSX.Element} The Hero component
 */
const Hero = () => {
  // State to control the logo intro animation
  const [showLogoIntro, setShowLogoIntro] = useState(false); // Start hidden
  const [showContent, setShowContent] = useState(false);
  const [logoFadingIn, setLogoFadingIn] = useState(false);
  const [logoFadingOut, setLogoFadingOut] = useState(false);

  // Effect to handle the animation timing
  useEffect(() => {
    // Wait 1 second before showing and fading in the logo
    const initialDelay = setTimeout(() => {
      setShowLogoIntro(true);
      setLogoFadingIn(true);
      
      // Logo fades in over 0.5s
      setTimeout(() => {
        setLogoFadingIn(false); // Logo has finished fading in
      }, 500);

      // Start the logo fade out after 2 seconds of being fully visible
      const fadeOutTimer = setTimeout(() => {
        setLogoFadingOut(true);
        
        // After 0.5s more (the fade-out duration), hide logo and show content
        setTimeout(() => {
          setShowLogoIntro(false);
          
          // Start showing content after logo is completely hidden
          setTimeout(() => {
            setShowContent(true);
          }, 100);
        }, 500);
      }, 2500);
    }, 1000); // 1 second initial delay

    return () => {
      clearTimeout(initialDelay);
    };
  }, []);

  return (
    <div className="hero-section">
      <div className="hero-overlay"></div>
      
      {/* Logo intro animation */}
      {showLogoIntro && (
        <div className={`logo-intro-container ${logoFadingIn ? 'fade-in' : ''} ${logoFadingOut ? 'fade-out' : ''}`}>
          <img src={Logo} alt="MET Connect® Logo" className="intro-logo" />
          <div className="intro-text-logo">
            <div className="intro-logo-main-text">MET Connect<sup>®</sup></div>
            <div className="intro-logo-sub-text">Connected Since 2010</div>
          </div>
        </div>
      )}

      {/* Regular hero content */}
      <Container className={`hero-content ${showContent ? 'fade-in' : 'hidden'}`}>
        <Row className="align-items-center">
          <Col lg={7} className="hero-text">
            <h1 className="hero-title">
              Secure Remote Access for Your Control System Network
            </h1>
          </Col>
          <div className="vertical-divider-container">
            <div className="vertical-divider"></div>
          </div>
          <Col>
            <p className="hero-subtitle">
              The MET Connect® Remote Access Service provides secure, reliable access to your 
              remotely located control system network without complex DNS record setup or static IP requirements.
            </p>
            <div className="hero-features">
              <div className="hero-feature">
                <FontAwesomeIcon icon={faLock} className="feature-icon" />
                <span>Enterprise-grade Security</span>
              </div>
              <div className="hero-feature">
                <FontAwesomeIcon icon={faServer} className="feature-icon" />
                <span>Comprehensive Monitoring</span>
              </div>
            </div>
            <div className="hero-buttons">
              <Button 
                variant="primary" 
                size="lg" 
                as={Link} 
                to="/remote-access"
                className="animated-btn"
              >
                <span className="animated-btn-content">
                  Explore Solutions
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </span>
              </Button>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Mini footer section */}
      <div className="hero-mini-footer">
        <div className="horizontal-divider"></div>
        <Container>
          <Row className="align-items-center">
            <Col md={4}>
              <p className="mini-footer-copyright">© 2025 MET Connect®. All rights reserved.</p>
            </Col>
            <Col md={4} className="text-md-center">
              <p className="mini-footer-credit">
                <a href="https://jamesboon.dev/" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faCode} className="me-2" />
                  Website by jamesboon.dev
                </a>
              </p>
            </Col>
            <Col md={4} className="text-md-end">
              <p className="mini-footer-contact">
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                <a href="mailto:info@met-connect.com">support@met-connect.com</a>
              </p>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default Hero;